import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { VideoAction } from '@textbook-api-client';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, throttleTime, withLatestFrom } from 'rxjs/operators';
import videojs from 'video.js';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../external/providers/textbook-actions-handler';
import {
  MEDIA_PLAYER_VOLUME_KEY,
  MediaPlayerPlaybackState,
  SourceType,
} from '../@base/components/media-player/media-player.component';
import { TextbookActionBase } from '../@base/textbook-action-base.model';
import { AudioState } from '../audio/audio.component';
import { GroupInfo } from '../../models/group-info';

@Component({
  selector: 'txtb-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VideoComponent extends TextbookActionBase<VideoAction> implements OnInit, OnDestroy {
  @Input() groupInfo!: GroupInfo;
  subscription = new Subscription();
  videoOptions: videojs.PlayerOptions = {};
  playerLayout = SourceType.Video;
  updatePlayback$ubject = new Subject<boolean>();
  updateControlsVisibility$ubject = new Subject<boolean>();
  updateCurrentTime$ubject = new Subject<number>();
  updateVolume$ubject = new Subject<number>();
  playerEnded$ubject = new Subject<void>();
  playerCurrentPlaybackState$ubject = new Subject<MediaPlayerPlaybackState>();
  playerCurrentTime$ubject = new Subject<number>();
  playerVolume$ubject = new Subject<number>();
  drawerContentCollapse$ubject = new Subject<boolean>();
  playAfterUncollapse = false;

  constructor(
    @Inject(TextbookActionsHandlerToken) public textbookActionsHandler: TextbookActionsHandler
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.action?.meta?.url) {
      return;
    }

    this.initMediaPlayer();

    this.subscription.add(
      this.drawerContentCollapse$ubject
        .pipe(withLatestFrom(this.playerCurrentPlaybackState$ubject))
        .subscribe(([isContentCollapse, currentPlaybackState]) => {
          if (currentPlaybackState === MediaPlayerPlaybackState.Played && !isContentCollapse) {
            this.updatePlayback$ubject.next(false);
            this.playAfterUncollapse = true;
          } else if (
            currentPlaybackState === MediaPlayerPlaybackState.Paused &&
            isContentCollapse &&
            this.playAfterUncollapse
          ) {
            this.updatePlayback$ubject.next(true);
            this.playAfterUncollapse = false;
          }
        })
    );

    this.subscription.add(
      this.playerVolume$ubject
        .pipe(debounceTime(500))
        .subscribe((volume) => localStorage.setItem(MEDIA_PLAYER_VOLUME_KEY, String(volume)))
    );

    this.subscription.add(
      this.playerCurrentTime$ubject.pipe(throttleTime(500)).subscribe((currentTime) =>
        this.textbookActionsHandler.txtbActions$ubject.next({
          actionId: this.action.id,
          state: {
            [this.action.type]: {
              currentTime,
            } as AudioState,
          },
        })
      )
    );

    this.subscription.add(
      this.playerEnded$ubject.subscribe(() => {
        this.textbookActionsHandler.txtbActions$ubject.next({
          actionId: this.action.id,
          state: {
            [this.action.type]: {
              currentTime: 0,
            } as AudioState,
          },
        });
      })
    );
  }

  initMediaPlayer(): void {
    this.videoOptions = {
      autoplay: false,
      controls: true,
      sources: [
        {
          src: this.action.meta.url,
          type: 'video/mp4',
        },
      ],
      html5: {
        playsinline: true,
      },
      controlBar: {
        children: [
          {
            name: 'playToggle',
          },
          {
            name: 'CurrentTimeDisplay',
          },
          {
            name: 'DurationDisplay',
          },
          {
            name: 'progressControl',
          },
          {
            name: 'VolumePanel',
          },
          {
            name: 'fullscreenToggle',
          },
        ],
      },
    };
  }

  onMediaCurrentTimeChange(currentTime: number): void {
    this.playerCurrentTime$ubject.next(currentTime);
  }

  onPlayerPlaybackChange(state: MediaPlayerPlaybackState): void {
    this.playerCurrentPlaybackState$ubject.next(state);
  }

  controlVideoPlayback(play: boolean): void {
    this.drawerContentCollapse$ubject.next(play);
    this.updateControlsVisibility$ubject.next(play);
  }

  onPlayerVolumeChange(volume: number): void {
    this.playerVolume$ubject.next(volume);
  }

  onPlayerEnded(): void {
    this.playerEnded$ubject.next();
  }

  onPlayerAfterViewInit(): void {
    const mediaPlayerVolume = localStorage.getItem(MEDIA_PLAYER_VOLUME_KEY) || 100;
    this.updateVolume$ubject.next(Number(mediaPlayerVolume));
  }

  onPlayerLoadedData(): void {
    this.subscription.add(
      this.textbookActionsHandler.overwriteTxtbAction$ubject
        .pipe(filter((value) => value.actionId === this.action.id))
        .subscribe(({ state }) =>
          this.updateCurrentTime$ubject.next(state[this.action.type]?.currentTime || 0)
        )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
